/**
 * CollisionShapeComponent - invisible physics collision geometry.
 * Inherits from MeshComponent to be a proper mesh-based component with invisible material.
 * Configure the collision shape using the `geometry` property (BoxGeometry, SphereGeometry, etc.)
 */

import * as THREE from 'three';

import { EngineClass } from '../systems/ClassRegistry.js';
import { property } from '../utils/serialization/decorator.js';

import { MeshComponent } from './visual/MeshComponent.js';

import type { MeshComponentOptions } from './visual/MeshComponent.js';

/**
 * Configuration options for the collision shape component
 */
export interface CollisionShapeComponentOptions extends MeshComponentOptions {
  /** Whether to enable debug visualization of the collision shape */
  enableDebugVisualization?: boolean;
}

/**
 * Invisible material for collision mesh (not rendered in game)
 */
const INVISIBLE_MATERIAL = new THREE.MeshBasicMaterial({
  visible: false,
  colorWrite: false,
  depthWrite: false
});

/**
 * Component that adds collision detection capability to an actor.
 * Creates invisible collision geometry for physics interaction.
 * The collision shape size is controlled by the component's transform scale.
 */
@EngineClass('CollisionShapeComponent')
export class CollisionShapeComponent extends MeshComponent {
  /** Whether debug visualization is currently enabled */
  private _enableDebugVisualization: boolean = false;

  @property()
  public get enableDebugVisualization(): boolean {
    return this._enableDebugVisualization;
  }

  public set enableDebugVisualization(value: boolean) {
    this._enableDebugVisualization = value;
    this.updateVisibility();
  }

  /** Wireframe material for debug visualization */
  private debugMaterial: THREE.Material | null = null;

  constructor() {
    super();
    this.material = INVISIBLE_MATERIAL.clone();
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.visible = false;
  }

  public override initialize(options?: CollisionShapeComponentOptions): void {
    super.initialize(options);

    this.createDebugMaterial();

    if (options?.enableDebugVisualization !== undefined) {
      this.enableDebugVisualization = options.enableDebugVisualization;
    }
    this.updateVisibility();
  }

  public override postLoad(): void {
    super.postLoad();
    this.createDebugMaterial();
    this.updateVisibility();
  }

  private createDebugMaterial(): void {
    this.debugMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      wireframe: true,
      transparent: true,
      opacity: 0.5
    });
  }

  private updateVisibility(): void {
    const shouldShowDebug = this.enableDebugVisualization || this.isEditor;

    if (shouldShowDebug && this.debugMaterial) {
      this.material = this.debugMaterial;
      this.mesh.visible = true;
    } else {
      this.material = INVISIBLE_MATERIAL.clone();
      this.mesh.visible = false;
    }
  }

  public override beginPlay(): void {
    super.beginPlay();
    this.updateVisibility();
  }

  public override onEditorAddToWorld(): void {
    super.onEditorAddToWorld();
    this.updateVisibility();
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_CollisionShape';
  }
}
